import { useCallback, useEffect, useState } from 'react';
import {
  DATA_REGIONS,
  REGION_PRESENTATION,
  type DataRegion,
  type RegistrationInfo,
} from '@lizrd/contracts';
import { autoRouteTarget, routeToRegion, writeRegionCookie } from '../../lib/region';

/**
 * Data-residency routing for the auth pages (docs/tech/07). On mount it settles the
 * visitor on the right regional host — redirecting to the host that serves their
 * saved/nearest region when that differs from this stack — and exposes a manual
 * region switch. `redirecting` lets the page hold its form while the browser bounces.
 */
export function useRegionRouting(info: RegistrationInfo | null): {
  redirecting: boolean;
  changeRegion: (region: DataRegion) => void;
} {
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!info) return;
    const target = autoRouteTarget(info);
    if (target) {
      setRedirecting(true);
      routeToRegion(info, target);
    } else {
      // Settled on this stack — persist so later loads skip geo and don't re-evaluate.
      writeRegionCookie(info.region);
    }
  }, [info]);

  const changeRegion = useCallback(
    (region: DataRegion) => {
      if (!info || region === info.region) return;
      // Cross-route to the other region's host. Locally (single host) there is nowhere
      // to go, so reflect the choice with a reload.
      if (!routeToRegion(info, region)) window.location.reload();
    },
    [info],
  );

  return { redirecting, changeRegion };
}

/** The region banner shown inside the signup / login forms: where the data lives,
 * and a one-tap switch to the other region (which cross-routes to its host). */
export function RegionBanner({
  info,
  context,
  onChange,
}: {
  info: RegistrationInfo;
  context: 'signup' | 'login';
  onChange: (region: DataRegion) => void;
}) {
  const current = REGION_PRESENTATION[info.region];
  const other = DATA_REGIONS.find((r) => r !== info.region) as DataRegion;
  const otherP = REGION_PRESENTATION[other];

  return (
    <div className="rounded-lg border border-hairline bg-surface-2 p-3">
      <div className="flex items-start gap-2.5">
        <span className="text-lg leading-none" aria-hidden>
          {current.flag}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-bone">
            {context === 'signup' ? (
              <>
                Your data will be hosted in the{' '}
                <b className="font-semibold">
                  {current.label} ({current.location})
                </b>{' '}
                region.
              </>
            ) : (
              <>
                Signing in to the{' '}
                <b className="font-semibold">
                  {current.label} ({current.location})
                </b>{' '}
                region.
              </>
            )}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted">
            {context === 'signup'
              ? 'Pick where your data lives — you can change it now, and it’s locked in once you create your account.'
              : 'Accounts live in the region chosen at signup.'}
          </p>
          <button
            type="button"
            onClick={() => onChange(other)}
            className="mt-1.5 text-xs font-medium text-iris hover:text-iris-strong"
          >
            {context === 'signup'
              ? `${otherP.flag} Prefer the ${otherP.label} (${otherP.location})? Switch region →`
              : `${otherP.flag} Account in the ${otherP.label}? Switch region →`}
          </button>
        </div>
      </div>
    </div>
  );
}
